import type { FsnbEditionsResponse } from '@shared/api';
import { fmtNum } from '@shared/lib/smetaLabels';
import { Badge } from '@shared/ui/Badge';
import { Card } from '@shared/ui/Card';
import { editionStatusLabel, editionStatusTone, fmtDate, total } from '../normBaseLabels';
import { BlockTitle, Grid, Note, SmallMuted, TableWrap, Wrap } from './styles';
import { UploadBlock } from './components/UploadBlock';
import { RegistryBlock } from './components/RegistryBlock';
import { EditionDetail } from './components/EditionDetail';

/** Вкладка «Редакции»: загрузка, реестр ФГИС ЦС, список и проверка редакций. */
export function NormBaseEditions({
  data,
  selectedId,
  onSelect,
}: {
  data: FsnbEditionsResponse | undefined;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const items = data?.items ?? [];
  const active = items.find((e) => e.status === 'active') ?? null;
  const selected = items.find((e) => e.id === selectedId) ?? null;

  return (
    <Wrap>
      {data?.can_upload && <UploadBlock />}
      <RegistryBlock
        canCheck={Boolean(data?.can_upload)}
        activeDate={active?.decree_date ?? null}
      />
      <Card>
        <BlockTitle>Редакции</BlockTitle>
        {items.length === 0 ? (
          <Note>Редакций пока нет — загрузите архив дополнения ФСНБ-2022.</Note>
        ) : (
          <TableWrap>
            <Grid>
              <thead>
                <tr>
                  <th>Редакция</th>
                  <th>Статус</th>
                  <th className="num">Норм</th>
                  <th>Загружена</th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr
                    key={e.id}
                    className={`clickable${e.id === selectedId ? ' selected' : ''}`}
                    onClick={() => onSelect(e.id)}
                  >
                    <td>
                      <strong>{e.title}</strong>
                      <SmallMuted>{e.decree ?? e.edition}</SmallMuted>
                    </td>
                    <td>
                      <Badge $tone={editionStatusTone(e.status)}>
                        {editionStatusLabel(e.status)}
                      </Badge>
                    </td>
                    <td className="num">
                      {e.status === 'importing' ? '…' : fmtNum(total(e.stats?.works))}
                    </td>
                    <td>
                      {fmtDate(e.imported_at ?? e.created_at)}
                      {e.uploaded_by_name && <SmallMuted>{e.uploaded_by_name}</SmallMuted>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Grid>
          </TableWrap>
        )}
      </Card>
      {selected && (
        <EditionDetail
          key={selected.id}
          edition={selected}
          canManage={Boolean(data?.can_manage)}
          activeTitle={active && active.id !== selected.id ? active.title : null}
          titleOf={(label) => items.find((e) => e.edition === label)?.title ?? label}
        />
      )}
    </Wrap>
  );
}

export default NormBaseEditions;
